import {NextRouter, useRouter} from 'next/router';
import {useMemo} from 'react';
import Url, {qs} from 'url-parse';
import {PromotionBannerContainer} from '../../../../common/containers/PromotionBannerContainer';
import {t} from '../../../../common/getTranslations';
import {isGreaterThanZero} from '../../../../common/isGreaterThanZero';
import {LogTabType} from '../../../../common/LogTabType.enum';
import {TabPositionSticky} from '../../../../common/presentational/TabPositionSticky';
import {requestContext} from '../../../../common/requestContext';
import {IS_CLIENT_SIDE} from '../../../../common/shared/utils/constants';
import useTypedSelector from '../../../../common/useTypedSelector';
import {MainTab} from '../../../../design-system/organisms/MainTab/MainTab';
import {MainTabItemType} from '../../../../design-system/organisms/MainTab/MainTabItemType.interface';
import {NoItem} from '../../../../design-system/organisms/NoItem/NoItem';
import {SubTab} from '../../../../design-system/organisms/SubTab/SubTab';
import {SubtabItemActiveColorScheme} from '../../../../design-system/organisms/SubTabItem/constants';
import {SubTabItemType} from '../../../../design-system/organisms/SubTabItem/SubTabItemType.interface';
import {EMPTY_LANDING_PRODUCT_TEXT_KEY} from '../../../../domain/landing/constant';
import {
  StaticLandingTodayNewSection,
  StaticLandingTodayNewSectionParam,
} from '../../../../graphql/generated/types';
import {TranslateKey} from '../../../../locale/type';
import {aggregateAndSendLog} from '../../../../logging/aggregateAndSendLog';
import {PageViewCustomPropsDataKey} from '../../../../logging/PageViewCustomPropsDataKey.enum';
import {putTiaraObjectProps} from '../../../../logging/putTiaraObjectProps';
import {selectIsSubtab} from '../../../../staticLanding/selector';
import {GnbMenuId} from '../../../menu/constant';
import {CommonSectionProps} from '../../type';

export function StaticLandingTodayNew({section, children}: CommonSectionProps) {
  const {isSubtab, isPreferTab} = useTypedSelector(state => ({
    isSubtab: selectIsSubtab({section, state}),
    isPreferTab: state.menu.selectedGnbMenuId === GnbMenuId.Prefer,
  }));
  const {isMobile, isWebView} = requestContext.requestInfo.display;

  const {
    param,
    subcategoryList: _subcategoryList,
    totalCount,
    promotionBanner,
    categoryTabList,
  } = section as Omit<StaticLandingTodayNewSection, 'groups'>;
  const router = useRouter();
  const {categoryUid, subcategory} = param;

  const categoryList: MainTabItemType[] = useMemo(
    () =>
      categoryTabList.map(({param, name}) => ({
        id: Number(param),
        text: name,
        textTKey: name as TranslateKey,
        href: `/landing/today-new/${param}`,
      })),
    [categoryTabList]
  );

  const categorySubtabList: SubTabItemType[] = useMemo(
    () =>
      categoryTabList.map(({param: id, name}) => {
        const {pathname, query} = getRouteWithParam({
          router,
          param,
          categoryUid: Number(id),
          isSubtab,
          isPreferTab,
        });
        let queryStr;
        if (query) {
          queryStr = qs.stringify(query);
        }
        return {
          name,
          param: String(id),
          href: `${pathname}${queryStr ? `?${queryStr}` : ''}`,
        };
      }),
    [categoryTabList, isPreferTab, isSubtab, param, router]
  );

  const subcategoryList: SubTabItemType[] = useMemo(
    () =>
      _subcategoryList.map(({name, param: subcategoryUid}) => {
        const {pathname, query} = getRouteWithParam({
          router,
          param,
          subcategoryUid,
          isSubtab,
          isPreferTab,
        });
        let queryStr;
        if (query) {
          queryStr = qs.stringify(query);
        }
        return {
          name,
          param: subcategoryUid,
          href: `${pathname}${queryStr ? `?${queryStr}` : ''}`,
        };
      }),
    [_subcategoryList, isPreferTab, isSubtab, param, router]
  );

  const currentCategoryName = useMemo(
    () =>
      categoryTabList.find(
        categoryInfo => categoryInfo.param === String(categoryUid)
      )?.name,
    [categoryTabList, categoryUid]
  );
  const currentSubcategoryName = subcategory?.name;

  return (
    <div
      className="flex flex-col w-full grow"
      {...putTiaraObjectProps({
        customProps: {
          page_category_filter: currentCategoryName,
          page_genre_filter: currentSubcategoryName,
        },
        click: {
          layer1: '섹션',
        },
      })}
      {...(isSubtab && {
        [PageViewCustomPropsDataKey.TabType]: 'tab_type=landing_single',
      })}
    >
      {!isSubtab && (
        <>
          <TabPositionSticky
            depth={1}
            isMobile={isMobile}
            isWebView={isWebView}
            disabled={!isMobile}
          >
            <MainTab
              isMobile={isMobile}
              title={t('landing_new')}
              tabItems={categoryList}
              currentTabItemId={categoryUid}
              logTabType={LogTabType.Category}
              showBorder={true}
              className={!isMobile ? 'px-15pxr' : undefined}
            />
          </TabPositionSticky>
          {subcategoryList && subcategory && (
            <TabPositionSticky
              depth={2}
              isMobile={isMobile}
              isWebView={isWebView}
              disabled={!isMobile}
            >
              <SubTab
                tabItems={subcategoryList}
                currentTabItemParam={subcategory.param}
                activeColorScheme={
                  !isMobile ? SubtabItemActiveColorScheme['2nd'] : undefined
                }
                onClickTab={(clickedTabItemId, e) => {
                  aggregateAndSendLog({
                    targetElem: e?.target as HTMLElement,
                    actionName: '장르_클릭',
                    actionType: 'Event',
                    click: {
                      copy: subcategoryList.find(
                        subcategory => subcategory.param === clickedTabItemId
                      )?.name,
                    },
                    customProps: {
                      user_action: 'click',
                    },
                  });
                }}
                logTabType={LogTabType.Genre}
                className={SUBTAB_STYLE}
                {...putTiaraObjectProps({
                  click: {
                    layer1: '서브탭2',
                  },
                })}
              />
            </TabPositionSticky>
          )}
        </>
      )}
      {isSubtab && isPreferTab && (
        <TabPositionSticky
          depth={3}
          isMobile={isMobile}
          isWebView={isWebView}
          disabled={!isMobile}
        >
          <SubTab
            tabItems={categorySubtabList}
            currentTabItemParam={String(categoryUid)}
            activeColorScheme={SubtabItemActiveColorScheme['2nd']}
            logTabType={LogTabType.Genre}
            className={SUBTAB_STYLE}
            onClickTab={(param, e) =>
              aggregateAndSendLog({
                targetElem: e?.target as HTMLElement,
                actionName: '카테고리_클릭',
                actionType: 'Event',
                click: {
                  layer1: '서브탭2',
                  copy: categorySubtabList.find(
                    category => category.param === param
                  )?.name,
                },
                customProps: {
                  user_action: 'click',
                },
              })
            }
          />
        </TabPositionSticky>
      )}
      {isSubtab && !isPreferTab && subcategoryList && subcategory && (
        <TabPositionSticky
          depth={3}
          isMobile={isMobile}
          isWebView={isWebView}
          disabled={!isMobile}
        >
          <SubTab
            tabItems={subcategoryList}
            currentTabItemParam={subcategory.param}
            activeColorScheme={SubtabItemActiveColorScheme['2nd']}
            logTabType={LogTabType.Genre}
            className={SUBTAB_STYLE}
            onClickTab={(param, e) =>
              aggregateAndSendLog({
                targetElem: e?.target as HTMLElement,
                actionName: '장르_클릭',
                actionType: 'Event',
                click: {
                  layer1: '서브탭2',
                  copy: subcategoryList.find(
                    subcategory => subcategory.param === param
                  )?.name,
                },
                customProps: {
                  user_action: 'click',
                },
              })
            }
          />
        </TabPositionSticky>
      )}
      {isSubtab && promotionBanner && (
        <PromotionBannerContainer promotionBanner={promotionBanner} />
      )}
      {isGreaterThanZero(totalCount) && <>{children}</>}
      {!isGreaterThanZero(totalCount) && (
        <NoItem isMobile={isMobile}>{t(EMPTY_LANDING_PRODUCT_TEXT_KEY)}</NoItem>
      )}
    </div>
  );
}

function getRouteWithParam({
  router,
  param,
  categoryUid: _categoryUid,
  subcategoryUid,
  isSubtab,
  isPreferTab,
}: {
  router: NextRouter;
  param: StaticLandingTodayNewSectionParam;
  categoryUid?: number;
  subcategoryUid?: string;
  isSubtab: boolean;
  isPreferTab: boolean;
}) {
  const {categoryUid} = param;
  let pathname, query;
  if (isSubtab) {
    // /menu/{menu_uid}/screen/{screen_uid}
    pathname = IS_CLIENT_SIDE
      ? window.location.pathname
      : new Url(router.asPath).pathname;
    if (isPreferTab) {
      query = {
        ...(_categoryUid && {category_uid: _categoryUid}),
      };
    } else {
      query = {
        ...(subcategoryUid && {subcategory_uid: subcategoryUid}),
      };
    }
  } else {
    pathname = `/landing/today-new/${_categoryUid ?? categoryUid}`;
    if (subcategoryUid !== undefined) {
      pathname += `/${subcategoryUid}`;
    }
  }

  return {pathname, query};
}

const SUBTAB_STYLE = 'mb-10pxr';
